import { ChangeDetectionStrategy, Component, Input } from "@angular/core";

/**
 * EVA Badge - Compteur numérique
 *
 * Composant 100% custom utilisant les tokens EVA.
 * Peut être utilisé standalone ou en overlay sur un autre élément.
 *
 * @example
 * // Badge standalone
 * <eva-badge value="5" severity="danger"></eva-badge>
 *
 * // Badge avec max (affiche "99+" si dépassé)
 * <eva-badge [value]="150" [max]="99"></eva-badge>
 *
 * // Badge dot (point sans valeur)
 * <eva-badge [dot]="true" severity="success"></eva-badge>
 *
 * // Badge en overlay
 * <div class="eva-badge-wrapper">
 *   <i class="pi pi-bell"></i>
 *   <eva-badge value="3" severity="danger" size="small"></eva-badge>
 * </div>
 */
@Component({
  selector: "eva-badge",
  standalone: true,
  imports: [],
  templateUrl: "./eva-badge.component.html",
  styleUrls: ["./eva-badge.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaBadgeComponent {
  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Valeur à afficher (nombre ou texte court) */
  @Input() value: string | number = "";

  /** Couleur/sévérité du badge */
  @Input() severity: "primary" | "secondary" | "success" | "warning" | "danger" | "info" = "primary";

  /** Taille du badge */
  @Input() size: "small" | "medium" | "large" = "medium";

  /** Affiche juste un point sans valeur */
  @Input() dot: boolean = false;

  /** Valeur maximale (affiche "X+" si dépassé) */
  @Input() max: number = 99;

  // ==========================================================================
  // Computed
  // ==========================================================================

  /**
   * Valeur à afficher (avec gestion du max)
   */
  get displayValue(): string {
    if (this.dot) return "";

    const num = Number(this.value);
    if (!isNaN(num) && num > this.max) {
      return `${this.max}+`;
    }
    return String(this.value);
  }
}
